export interface ApplicationQueueRecord {
  status?: string | null;
  is_profile_incomplete?: boolean | null;
  queue_status?: string | null;
  ba?: {
    status?: string | null;
  } | null;
}

export type ApplicationQueueTab = 'submitted' | 'not_submitted' | 'outstanding' | 'rejected';

/** Submitted work takes precedence over stale incomplete flags; drafts are not submissions. */
export const getApplicationQueueTab = (
  application: ApplicationQueueRecord
): ApplicationQueueTab => {
  if (application.status === 'submitted') return 'submitted';
  if (application.status === 'rejected') {
    if (application.ba?.status === 'rejected') return 'rejected';
    return application.is_profile_incomplete || application.ba?.status === 'profile_incomplete'
      ? 'outstanding'
      : 'rejected';
  }
  return 'not_submitted';
};

export interface PromoterProfileCandidate {
  id: string;
  id_passport_number?: string | null;
  cell_number?: string | null;
  created_at?: string | null;
}

export interface ApplicationQueueCandidate extends ApplicationQueueRecord {
  user_id?: string | null;
}

export interface ApplicationQueueBACandidate {
  user_id?: string | null;
  status?: string | null;
}

export const hasPromoterIdentity = (profile?: PromoterProfileCandidate | null) =>
  Boolean(
    String(profile?.id_passport_number ?? '').trim() || String(profile?.cell_number ?? '').trim()
  );

export const buildSyntheticPendingApplication = (profile: PromoterProfileCandidate, ba?: any) => ({
  id: `profile-incomplete-${profile.id}`,
  user_id: profile.id,
  org_id: ba?.org_id ?? null,
  status: ba?.status === 'rejected' ? 'rejected' : 'draft',
  queue_status:
    ba?.status === 'profile_incomplete'
      ? 'profile_incomplete'
      : ba?.status === 'rejected'
        ? 'rejected'
        : 'pending',
  completion_percentage: 0,
  submitted_at: null,
  created_at: profile.created_at ?? null,
  form_json: {},
  ba: ba ?? null,
  profile,
  is_profile_incomplete: true,
  is_synthetic: true,
});

/**
 * Applications contains work for reviewers, not a directory of approved BAs.
 * A sent-back application is represented by a rejected application row plus a
 * profile_incomplete BA row, so that state must remain actionable.
 */
export const isActionableApplication = (application: ApplicationQueueRecord) => {
  if (application.status === 'approved') return false;

  return (
    application.status === 'submitted' ||
    application.status === 'draft' ||
    Boolean(application.is_profile_incomplete) ||
    application.ba?.status === 'profile_incomplete'
  );
};

/**
 * Returns the unique users visible in the Applications work queue. Keeping this
 * calculation shared prevents dashboard and navigation counters from drifting.
 */
export const getActionableApplicationUserIds = ({
  applications,
  bas,
  profiles,
}: {
  applications: ApplicationQueueCandidate[];
  bas: ApplicationQueueBACandidate[];
  profiles: PromoterProfileCandidate[];
}) => {
  const baByUserId = new Map(
    bas.filter((ba) => ba.user_id).map((ba) => [ba.user_id as string, ba])
  );
  const approvedUserIds = new Set(
    applications
      .filter((application) => application.status === 'approved' && application.user_id)
      .map((application) => application.user_id as string)
  );

  bas
    .filter((ba) => ba.status === 'approved' && ba.user_id)
    .forEach((ba) => approvedUserIds.add(ba.user_id as string));

  const visibleApplications = applications.filter(
    (application) => application.user_id && !approvedUserIds.has(application.user_id)
  );
  const applicationUserIds = new Set(
    visibleApplications.map((application) => application.user_id as string)
  );
  const actionableUserIds = new Set<string>();

  visibleApplications.forEach((application) => {
    const userId = application.user_id as string;
    const ba = baByUserId.get(userId) ?? null;

    if (
      isActionableApplication({
        ...application,
        ba,
        is_profile_incomplete:
          Boolean(application.is_profile_incomplete) ||
          ba?.status === 'profile_incomplete' ||
          application.status === 'draft',
      })
    ) {
      actionableUserIds.add(userId);
    }
  });

  profiles.forEach((profile) => {
    if (
      hasPromoterIdentity(profile) &&
      !approvedUserIds.has(profile.id) &&
      !applicationUserIds.has(profile.id)
    ) {
      actionableUserIds.add(profile.id);
    }
  });

  return actionableUserIds;
};
